import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

const Container = styled.div`
  min-height: 100vh;
  padding: 6rem 4rem 2rem;
  background: linear-gradient(
    135deg,
    rgba(0, 0, 0, 0.95) 0%,
    rgba(0, 0, 0, 0.8) 100%
  );
  position: relative;
  overflow-x: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(
      circle at 50% 0%,
      rgba(64, 224, 208, 0.05) 0%,
      transparent 50%
    );
    pointer-events: none;
  }
`

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  display: flex;
  gap: 2rem;
`

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: 2rem;
  background: linear-gradient(135deg, var(--color-text) 0%, var(--color-primary) 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-align: center;
`

const TableOfContents = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1.5rem;
  position: fixed;
  top: 90px;
  width: 300px;
  height: calc(100vh - 110px);
  overflow-y: auto;
  z-index: 10;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`

const TOCTitle = styled.h3`
  font-size: 1.25rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
`

const TOCList = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0;

  li {
    margin-bottom: 0.5rem;
    cursor: pointer;
    color: var(--color-text-secondary);
    transition: color 0.2s ease;

    &:hover {
      color: var(--color-primary);
    }

    ul {
      margin-left: 1rem;
      margin-top: 0.5rem;
    }

    &[data-active="true"] {
      color: var(--color-primary);
      font-weight: 500;
    }
  }
`

const MainContent = styled.div`
  flex: 1;
  margin-left: 320px;
`

const Content = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 2rem;
`

const Section = styled.section`
  margin-bottom: 3rem;
  scroll-margin-top: 100px;

  &:last-child {
    margin-bottom: 0;
  }
`

const SectionTitle = styled.h2`
  font-size: 1.75rem;
  color: var(--color-primary);
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.75rem;

  svg {
    width: 24px;
    height: 24px;
  }
`

const SubSection = styled.div`
  margin: 2rem 0;
  padding-left: 1rem;
  border-left: 2px solid rgba(64, 224, 208, 0.2);
  scroll-margin-top: 100px;

  &:hover {
    border-left-color: var(--color-primary);
  }
`

const SubSectionTitle = styled.h3`
  font-size: 1.25rem;
  color: var(--color-primary);
  margin-bottom: 1rem;
  display: flex;
  align-items: center;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    background: var(--color-primary);
    border-radius: 50%;
    margin-right: 0.75rem;
    margin-left: -1.4rem;
  }
`

const Text = styled.p`
  color: var(--color-text-secondary);
  line-height: 1.8;
  margin-bottom: 1.5rem;
  font-size: 1.1rem;

  &:last-child {
    margin-bottom: 0;
  }
`

const List = styled.ul`
  list-style-type: none;
  padding: 0;
  margin: 0 0 1.5rem;

  li {
    color: var(--color-text-secondary);
    margin-bottom: 1rem;
    padding-left: 1.5rem;
    position: relative;
    line-height: 1.6;

    &::before {
      content: "•";
      color: var(--color-primary);
      position: absolute;
      left: 0;
    }
  }
`

const CodeBlock = styled.pre`
  background: rgba(0, 0, 0, 0.3);
  padding: 1.5rem;
  border-radius: 8px;
  overflow-x: auto;
  margin: 1.5rem 0;
  font-family: 'Fira Code', monospace;
  font-size: 0.9rem;
  line-height: 1.5;
  color: #e2e8f0;
  border: 1px solid rgba(64, 224, 208, 0.2);
`

const Card = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1.5rem;
  margin: 1rem 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--color-primary);
    transform: translateY(-2px);
  }
`

const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.$primary ? 
    'linear-gradient(135deg, var(--color-primary), var(--color-secondary))' : 
    'rgba(255, 255, 255, 0.1)'};
  color: ${props => props.$primary ? 'white' : 'var(--color-text)'};
  border: 1px solid ${props => props.$primary ? 
    'transparent' : 'rgba(255, 255, 255, 0.2)'};

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }
`

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin: 1.5rem 0;
`

export default function Guide() {
  const [activeSection, setActiveSection] = useState('getting-started')
  const navigate = useNavigate()

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id)
          }
        })
      },
      {
        rootMargin: '-20% 0px -80% 0px'
      }
    )

    const sections = document.querySelectorAll('section[id]')
    sections.forEach((section) => observer.observe(section))

    return () => {
      sections.forEach((section) => observer.unobserve(section))
    }
  }, [])

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      const offset = 100
      const elementPosition = element.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - offset

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      })
      setActiveSection(sectionId)
    }
  }

  return (
    <Container>
      <Title>Platform Guide</Title>
      <ContentWrapper>
        <TableOfContents>
          <TOCTitle>Quick Navigation</TOCTitle>
          <TOCList>
            <li 
              onClick={() => scrollToSection('getting-started')}
              data-active={activeSection === 'getting-started'}
            >
              1. Getting Started
              <ul>
                <li onClick={() => scrollToSection('wallet-setup')}>Wallet Setup</li>
                <li onClick={() => scrollToSection('platform-overview')}>Platform Overview</li>
                <li onClick={() => scrollToSection('first-project')}>Your First Project</li>
              </ul>
            </li>
            <li 
              onClick={() => scrollToSection('project-creation')}
              data-active={activeSection === 'project-creation'}
            >
              2. Project Creation
              <ul>
                <li onClick={() => scrollToSection('templates')}>Project Templates</li>
                <li onClick={() => scrollToSection('custom-projects')}>Custom Projects</li>
                <li onClick={() => scrollToSection('agent-config')}>Agent Configuration</li>
              </ul>
            </li>
            <li 
              onClick={() => scrollToSection('monitoring')}
              data-active={activeSection === 'monitoring'}
            >
              3. Project Monitoring
              <ul>
                <li onClick={() => scrollToSection('dashboard')}>Dashboard Overview</li>
                <li onClick={() => scrollToSection('visualization')}>Network Visualization</li>
                <li onClick={() => scrollToSection('feedback')}>Providing Feedback</li>
              </ul>
            </li>
            <li 
              onClick={() => scrollToSection('advanced')}
              data-active={activeSection === 'advanced'}
            >
              4. Advanced Features
              <ul>
                <li onClick={() => scrollToSection('api-integration')}>API Integration</li>
                <li onClick={() => scrollToSection('automation')}>Workflow Automation</li>
                <li onClick={() => scrollToSection('customization')}>Platform Customization</li>
              </ul>
            </li>
            <li 
              onClick={() => scrollToSection('support')}
              data-active={activeSection === 'support'}
            >
              5. Support & Resources
              <ul>
                <li onClick={() => scrollToSection('faq')}>FAQ</li>
                <li onClick={() => scrollToSection('troubleshooting')}>Troubleshooting</li>
                <li onClick={() => scrollToSection('contact')}>Contact Support</li>
              </ul>
            </li>
          </TOCList>
        </TableOfContents>

        <MainContent>
          <Content>
            <Section id="getting-started">
              <SectionTitle>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
                Getting Started
              </SectionTitle>
              <Text>
                Welcome to OmniBrain! This comprehensive guide will help you understand
                and utilize all features of our platform effectively. Follow these steps
                to begin your journey with AI-powered project development.
              </Text>

              <SubSection id="wallet-setup">
                <SubSectionTitle>Wallet Setup</SubSectionTitle>
                <Text>
                  To access all platform features, you'll need to connect your Solana wallet.
                  We support multiple wallet providers:
                </Text>
                <Grid>
                  <Card>
                    <h4>Phantom Wallet</h4>
                    <Text>Most popular Solana wallet with extensive features</Text>
                    <Button $primary onClick={() => window.open('https://phantom.app', '_blank')}>
                      Install Phantom
                    </Button>
                  </Card>
                  <Card>
                    <h4>Solflare</h4>
                    <Text>Secure wallet with hardware support</Text>
                    <Button $primary onClick={() => window.open('https://solflare.com', '_blank')}>
                      Install Solflare
                    </Button>
                  </Card>
                </Grid>
              </SubSection>

              <SubSection id="platform-overview">
                <SubSectionTitle>Platform Overview</SubSectionTitle>
                <Text>
                  OmniBrain consists of several key components working together:
                </Text>
                <List>
                  <li>AI Agents: Specialized workers for different tasks</li>
                  <li>Project Dashboard: Central hub for monitoring progress</li>
                  <li>Network Visualization: Real-time agent collaboration view</li>
                  <li>Feedback System: Interactive project refinement tools</li>
                </List>
              </SubSection>

              <SubSection id="first-project">
                <SubSectionTitle>Your First Project</SubSectionTitle>
                <Text>
                  Starting your first project is simple:
                </Text>
                <List>
                  <li>Click "Request a Project" in the navigation bar</li>
                  <li>Choose a project template or create a custom request</li>
                  <li>Configure AI agents based on your needs</li>
                  <li>Review and approve the project scope</li>
                  <li>Monitor progress through the dashboard</li>
                </List>
                <Button onClick={() => navigate('/request')}>
                  Start Your First Project
                </Button>
              </SubSection>
            </Section>

            <Section id="project-creation">
              <SectionTitle>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
                Project Creation
              </SectionTitle>
              
              <SubSection id="templates">
                <SubSectionTitle>Project Templates</SubSectionTitle>
                <Text>
                  We offer pre-configured templates for common project types:
                </Text>
                <Grid>
                  <Card>
                    <h4>Website Development</h4>
                    <Text>Full-stack web applications with modern technologies</Text>
                    <CodeBlock>
{`{
  "template": "website",
  "stack": {
    "frontend": "React",
    "backend": "Node.js",
    "database": "PostgreSQL"
  }
}`}
                    </CodeBlock>
                  </Card>
                  <Card>
                    <h4>Social Media Management</h4>
                    <Text>Automated content creation and engagement</Text>
                    <CodeBlock>
{`{
  "template": "social_media",
  "platforms": [
    "twitter",
    "instagram",
    "linkedin"
  ]
}`}
                    </CodeBlock>
                  </Card>
                </Grid>
              </SubSection>

              <SubSection id="custom-projects">
                <SubSectionTitle>Custom Projects</SubSectionTitle>
                <Text>
                  For unique requirements, you can create a custom project:
                </Text>
                <List>
                  <li>Define project scope and objectives</li>
                  <li>Select required technologies and tools</li>
                  <li>Specify deliverables and timelines</li>
                  <li>Configure custom agent behaviors</li>
                </List>
              </SubSection>

              <SubSection id="agent-config">
                <SubSectionTitle>Agent Configuration</SubSectionTitle>
                <Text>
                  Fine-tune AI agents for optimal performance:
                </Text>
                <CodeBlock>
{`{
  "agents": {
    "coder": {
      "count": 3,
      "specializations": ["frontend", "backend", "database"],
      "priority": "high"
    },
    "designer": {
      "count": 1,
      "tools": ["figma", "photoshop"],
      "style": "modern"
    }
  }
}`}
                </CodeBlock>
              </SubSection>
            </Section>

            <Section id="monitoring">
              <SectionTitle>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
                </svg>
                Project Monitoring
              </SectionTitle>
              
              <SubSection id="dashboard">
                <SubSectionTitle>Dashboard Overview</SubSectionTitle>
                <Text>
                  The project dashboard provides real-time insights:
                </Text>
                <List>
                  <li>Project status and progress indicators</li>
                  <li>Active agent tasks and completions</li>
                  <li>Resource utilization metrics</li>
                  <li>Timeline and milestone tracking</li>
                </List>
              </SubSection>

              <SubSection id="visualization">
                <SubSectionTitle>Network Visualization</SubSectionTitle>
                <Text>
                  Our 3D visualization shows agent collaboration in real-time:
                </Text>
                <List>
                  <li>Agent interactions and communication flows</li>
                  <li>Task dependencies and relationships</li>
                  <li>Resource allocation and bottlenecks</li>
                  <li>Progress indicators and status updates</li>
                </List>
              </SubSection>

              <SubSection id="feedback">
                <SubSectionTitle>Providing Feedback</SubSectionTitle>
                <Text>
                  Interact with agents to refine project direction:
                </Text>
                <List>
                  <li>Real-time feedback on deliverables</li>
                  <li>Project requirement adjustments</li>
                  <li>Style and preference specifications</li>
                  <li>Priority and focus area updates</li>
                </List>
              </SubSection>
            </Section>

            <Section id="advanced">
              <SectionTitle>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58c.18-.14.23-.41.12-.61l-1.92-3.32c-.12-.22-.37-.29-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54c-.04-.24-.24-.41-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58c-.18.14-.23.41-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
                </svg>
                Advanced Features
              </SectionTitle>

              <SubSection id="api-integration">
                <SubSectionTitle>API Integration</SubSectionTitle>
                <Text>
                  Integrate OmniBrain with your existing tools:
                </Text>
                <CodeBlock>
{`curl -X POST https://api.omnibrain.ai/v1/projects \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "name": "Automated Website Build",
    "type": "website_development",
    "config": {
      "agents": {
        "frontend": 2,
        "backend": 1
      }
    }
  }'`}
                </CodeBlock>
              </SubSection>

              <SubSection id="automation">
                <SubSectionTitle>Workflow Automation</SubSectionTitle>
                <Text>
                  Create automated workflows using our event system:
                </Text>
                <CodeBlock>
{`{
  "trigger": "milestone_completed",
  "conditions": {
    "project_type": "website",
    "milestone": "frontend_mvp"
  },
  "actions": [
    {
      "type": "notify_slack",
      "channel": "project-updates"
    },
    {
      "type": "deploy_preview",
      "environment": "staging"
    }
  ]
}`}
                </CodeBlock>
              </SubSection>

              <SubSection id="customization">
                <SubSectionTitle>Platform Customization</SubSectionTitle>
                <Text>
                  Customize the platform to match your workflow:
                </Text>
                <List>
                  <li>Custom agent templates and behaviors</li>
                  <li>Workflow and pipeline configurations</li>
                  <li>Integration with existing tools</li>
                  <li>Custom reporting and analytics</li>
                </List>
              </SubSection>
            </Section>

            <Section id="support">
              <SectionTitle>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.5 2C6.81 2 3 5.81 3 10.5c0 2.47 1.49 4.7 3.18 6.51l.31.3.03-.31c.14-1.42.82-2.48 1.53-3.27.68-.75 1.4-1.23 1.95-1.23.56 0 1.28.48 1.96 1.23.71.79 1.39 1.85 1.53 3.27l.03.31.31-.3c1.69-1.81 3.18-4.04 3.18-6.51C17 5.81 13.19 2 8.5 2zm0 7c-.83 0-1.5-.67-1.5-1.5S10.67 6 11.5 6s1.5.67 1.5 1.5S12.33 9 11.5 9z"/>
                </svg>
                Support & Resources
              </SectionTitle>

              <SubSection id="faq">
                <SubSectionTitle>Frequently Asked Questions</SubSectionTitle>
                <Grid>
                  <Card>
                    <h4>How do I get started?</h4>
                    <Text>
                      Connect your wallet, browse available templates, and create
                      your first project using our guided setup process.
                    </Text>
                  </Card>
                  <Card>
                    <h4>What types of projects can I create?</h4>
                    <Text>
                      From web development to social media management, our AI agents
                      can handle a wide range of digital projects.
                    </Text>
                  </Card>
                </Grid>
              </SubSection>

              <SubSection id="troubleshooting">
                <SubSectionTitle>Troubleshooting</SubSectionTitle>
                <Text>
                  Common issues and their solutions:
                </Text>
                <List>
                  <li>Wallet connection issues: Refresh browser and try again</li>
                  <li>Agent unresponsive: Check project configuration</li>
                  <li>Deployment failed: Verify resource requirements</li>
                  <li>Network errors: Check internet connection</li>
                </List>
              </SubSection>

              <SubSection id="contact">
                <SubSectionTitle>Contact Support</SubSectionTitle>
                <Text>
                  Need additional help? Our support team is available 24/7:
                </Text>
                <Grid>
                  <Card>
                    <h4>Email Support</h4>
                    <Text>support@omnibrain.ai</Text>
                    <Button $primary onClick={() => window.location.href = 'mailto:support@omnibrain.ai'}>
                      Send Email
                    </Button>
                  </Card>
                  <Card>
                    <h4>Live Chat</h4>
                    <Text>Available during business hours</Text>
                    <Button onClick={() => navigate('/support')}>
                      Start Chat
                    </Button>
                  </Card>
                </Grid>
              </SubSection>
            </Section>
          </Content>
        </MainContent>
      </ContentWrapper>
    </Container>
  )
}
